import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../support/store";
import { ModelConfig } from "../RbacConfig";

type AuthzScopes = {
  products?: number[];
  goToMarkets?: number[];
  [key: string]: any;
};

type AuthzState = {
  permissions?: string[];
  scopes?: AuthzScopes;
  isAdmin?: boolean;
  is_admin?: boolean;
  adminAccess?: boolean;
  admin_access?: boolean;
  [key: string]: any;
};

export const useAdminPermissions = () => {
  const authz = useSelector((state: RootState): AuthzState => {
    return (state.user?.authz || {}) as AuthzState;
  });

  const scopes = useMemo<AuthzScopes>(() => {
    return authz.scopes || {};
  }, [authz.scopes]);

  const permissions = useMemo<string[]>(() => {
    return Array.isArray(authz.permissions) ? authz.permissions : [];
  }, [authz.permissions]);

  const permSet = useMemo(() => {
    return new Set<string>(permissions);
  }, [permissions]);

  const isAdminFull = useMemo(() => {
    return (
      permSet.has("admin.full") ||
      authz.isAdmin === true ||
      authz.is_admin === true
    );
  }, [permSet, authz.isAdmin, authz.is_admin]);

  const hasPerm = (permissionName?: string) => {
    if (!permissionName) return false;
    if (isAdminFull) return true;
    return permSet.has(permissionName);
  };

  const hasAnyPerm = (permissionNames: Array<string | undefined>) => {
    if (isAdminFull) return true;

    return permissionNames
      .filter(Boolean)
      .some((permissionName) => hasPerm(permissionName));
  };

  const scopeIds = (scopeKey?: "products" | "goToMarkets"): number[] => {
    if (!scopeKey) return [];

    const ids = scopes?.[scopeKey];

    return Array.isArray(ids) ? ids : [];
  };

  const canReadModel = (model: ModelConfig) => {
    if (isAdminFull) return true;
    return hasPerm(model.perms?.read);
  };

  const canCreateModel = (model: ModelConfig) => {
    if (isAdminFull) return true;
    return hasPerm(model.perms?.create);
  };

  const canUpdateModel = (model: ModelConfig) => {
    if (isAdminFull) return true;
    return hasPerm(model.perms?.update);
  };

  const canDeleteModel = (model: ModelConfig) => {
    if (isAdminFull) return true;
    return hasPerm(model.perms?.delete);
  };

  const canSeeTab = (model: ModelConfig) => {
    if (isAdminFull) return true;

    if (model.perms?.pageView && hasPerm(model.perms.pageView)) {
      return true;
    }

    return hasAnyPerm([
      model.perms?.read,
      model.perms?.create,
      model.perms?.update,
      model.perms?.delete,
    ]);
  };

  const hasAnyScopeForTab = (model: ModelConfig) => {
    if (!model.scopeKey) return true;
    if (isAdminFull) return true;

    return scopeIds(model.scopeKey).length > 0;
  };

  const isReadOnlyModel = (model: ModelConfig) => {
    if (!canSeeTab(model)) return false;
    if (canUpdateModel(model)) return false;

    return canReadModel(model);
  };

  const hasProductScope = isAdminFull || scopeIds("products").length > 0;
  const hasGtmScope = isAdminFull || scopeIds("goToMarkets").length > 0;

  const isVisibleTab = (model?: ModelConfig | any): boolean => {
    if (!model) return false;

    if (model.perms) {
      return canSeeTab(model) && hasAnyScopeForTab(model);
    }

    if (model.pageView) {
      return hasPerm(model.pageView);
    }

    return false;
  };

  return {
    authz,
    scopes,
    permissions,
    permSet,

    isAdminFull,

    hasPerm,
    hasAnyPerm,

    canReadModel,
    canCreateModel,
    canUpdateModel,
    canDeleteModel,
    isReadOnlyModel,

    scopeIds,
    hasProductScope,
    hasGtmScope,

    isVisibleTab,
  };
};